import React, { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Wand2,
  ImagePlus,
  Video,
  Loader2,
  Trash2,
  Download,
  X,
  Sparkles,
  BookmarkPlus,
  Bookmark,
  Play,
  Library,
  Image as ImageIcon,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { toast } from "sonner";
import { generationsApi, promptsApi, mediaUrl, sessionsApi, IMAGE_MODELS, VIDEO_MODELS, ASPECT_RATIOS } from "../lib/api";
import { FilePicker } from "./FilePicker";

/** Polls a video job until done. Returns final generation object. */
function pollVideoStatus(id, onUpdate, signal) {
  return new Promise((resolve, reject) => {
    let attempts = 0;
    const tick = async () => {
      if (signal?.aborted) return reject(new Error("aborted"));
      attempts += 1;
      try {
        const res = await generationsApi.videoStatus(id);
        onUpdate?.(res.generation, attempts);
        if (res.generation.status === "done") resolve(res.generation);
        else if (res.generation.status === "failed")
          reject(new Error(res.generation.error || "Generation failed"));
        else if (attempts > 40) reject(new Error("Video generation timed out"));
        else setTimeout(tick, 6000);
      } catch (e) {
        if (attempts > 40) reject(e);
        else setTimeout(tick, 6000);
      }
    };
    tick();
  });
}

const AspectPicker = ({ value, onChange, testIdPrefix }) => (
  <div className="flex items-center gap-2 flex-wrap" data-testid={`${testIdPrefix}-picker`}>
    <div
      role="radiogroup"
      aria-label="Aspect ratio"
      className="inline-flex rounded-lg border border-white/10 bg-black/30 p-0.5"
    >
      {ASPECT_RATIOS.map((r) => {
        const active = value === r.value;
        return (
          <button
            key={r.value}
            type="button"
            role="radio"
            aria-checked={active}
            data-testid={`${testIdPrefix}-${r.value.replace(":", "x")}`}
            onClick={() => onChange(r.value)}
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs transition-colors ${
              active
                ? "bg-blue-500/20 text-blue-100 shadow-inner shadow-blue-500/20"
                : "text-white/60 hover:text-white hover:bg-white/5"
            }`}
          >
            <span
              className={`block border ${
                active ? "border-blue-300/70" : "border-white/40"
              }`}
              style={
                r.value === "16:9"
                  ? { width: 14, height: 8, borderRadius: 1.5 }
                  : r.value === "9:16"
                  ? { width: 8, height: 14, borderRadius: 1.5 }
                  : { width: 11, height: 11, borderRadius: 1.5 }
              }
            />
            {r.label}
          </button>
        );
      })}
    </div>
    <span className="text-[10px] text-white/40 uppercase tracking-widest">
      Aspect
    </span>
  </div>
);

export const StudioPanel = ({ open, onOpenChange, activeSessionId, user }) => {
  const [tab, setTab] = useState("image");
  const [imagePrompt, setImagePrompt] = useState("");
  const [videoPrompt, setVideoPrompt] = useState("");
  const [duration, setDuration] = useState(6);
  const [imageModel, setImageModel] = useState(() => {
    if (typeof window === "undefined") return IMAGE_MODELS[0].value;
    return window.localStorage.getItem("glitch_image_model") || IMAGE_MODELS[0].value;
  });
  const [videoModel, setVideoModel] = useState(() => {
    if (typeof window === "undefined") return VIDEO_MODELS[0].value;
    return window.localStorage.getItem("glitch_video_model") || VIDEO_MODELS[0].value;
  });
  const [imageAspect, setImageAspect] = useState(() => {
    if (typeof window === "undefined") return "1:1";
    return window.localStorage.getItem("glitch_image_aspect") || "1:1";
  });
  const [videoAspect, setVideoAspect] = useState(() => {
    if (typeof window === "undefined") return "16:9";
    return window.localStorage.getItem("glitch_video_aspect") || "16:9";
  });
  const [useAvatarImage, setUseAvatarImage] = useState(false);
  const [useAvatarVideo, setUseAvatarVideo] = useState(false);
  const [selectedAvatarImage, setSelectedAvatarImage] = useState("");
  const [selectedAvatarVideo, setSelectedAvatarVideo] = useState("");
  const [uploadedRefImage, setUploadedRefImage] = useState(null); // {id, filename, ...}
  const [filePickerOpen, setFilePickerOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pending, setPending] = useState(null);
  const abortRef = useRef(null);

  // Persist the model choices
  useEffect(() => {
    try {
      window.localStorage.setItem("glitch_image_model", imageModel);
    } catch (_) { /* ignore */ }
  }, [imageModel]);
  useEffect(() => {
    try {
      window.localStorage.setItem("glitch_video_model", videoModel);
    } catch (_) { /* ignore */ }
  }, [videoModel]);
  useEffect(() => {
    try {
      window.localStorage.setItem("glitch_image_aspect", imageAspect);
    } catch (_) { /* ignore */ }
  }, [imageAspect]);
  useEffect(() => {
    try {
      window.localStorage.setItem("glitch_video_aspect", videoAspect);
    } catch (_) { /* ignore */ }
  }, [videoAspect]);

  const load = useCallback(async () => {
    try {
      const [g, p] = await Promise.all([
        generationsApi.list(),
        promptsApi.list(),
      ]);
      setItems(g.generations || []);
      setPrompts(p.prompts || []);
    } catch (e) {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  // Listen for voice-triggered generations so the panel auto-refreshes
  useEffect(() => {
    const onGen = (e) => {
      const g = e.detail;
      if (!g) return;
      setItems((prev) => {
        const exists = prev.some((x) => x.id === g.id);
        return exists ? prev.map((x) => (x.id === g.id ? g : x)) : [g, ...prev];
      });
      // If it's a pending video, kick off polling here too so it lands even
      // if the user never opens the panel
      if (g.kind === "video" && g.status === "pending") {
        pollVideoStatus(g.id, (upd) => {
          setItems((prev) => prev.map((x) => (x.id === upd.id ? upd : x)));
        }).catch(() => {});
      }
    };
    window.addEventListener("glitch:generation", onGen);
    return () => window.removeEventListener("glitch:generation", onGen);
  }, []);

  const generateImage = async () => {
    const p = imagePrompt.trim();
    if (!p || loading) return;
    setLoading(true);
    setPending({ kind: "image", prompt: p, status: "pending" });
    try {
      const res = await generationsApi.createImage(
        p,
        imageModel,
        useAvatarImage,
        selectedAvatarImage || undefined,
        imageAspect,
        uploadedRefImage?.id
      );
      setItems((prev) => [res.generation, ...prev]);
      toast.success("Image ready");
      setImagePrompt("");
      if (activeSessionId && res.generation.file_url) {
        try {
          await sessionsApi.appendMessage(activeSessionId, {
            role: "assistant",
            text: `Here you go — ${p}`,
            attachment: {
              kind: "image",
              file_url: res.generation.file_url,
              prompt: p,
              generation_id: res.generation.id,
            },
          });
          window.dispatchEvent(new CustomEvent("glitch:transcript-refresh"));
        } catch (_) {
          /* ignore */
        }
      }
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || e.message || "Image generation failed"
      );
    } finally {
      setLoading(false);
      setPending(null);
    }
  };

  const generateVideo = async () => {
    const p = videoPrompt.trim();
    if (!p || loading) return;
    setLoading(true);
    const controller = new AbortController();
    abortRef.current = controller;
    setPending({ kind: "video", prompt: p, status: "pending", elapsed: 0 });
    try {
      const res = await generationsApi.createVideo(
        p,
        duration,
        videoModel,
        useAvatarVideo,
        selectedAvatarVideo || undefined,
        videoAspect
      );
      const id = res.generation.id;
      setItems((prev) => [res.generation, ...prev]);
      // Attach a "rendering…" placeholder to the transcript right away
      if (activeSessionId) {
        try {
          await sessionsApi.appendMessage(activeSessionId, {
            role: "assistant",
            text: `Rendering a ${duration}s clip of ${p}…`,
            attachment: {
              kind: "video",
              file_url: null,
              prompt: p,
              generation_id: id,
              status: "pending",
            },
          });
          window.dispatchEvent(new CustomEvent("glitch:transcript-refresh"));
        } catch (_) {
          /* ignore */
        }
      }
      const finalGen = await pollVideoStatus(
        id,
        (g, attempt) => {
          setPending({
            kind: "video",
            prompt: p,
            status: g.status,
            elapsed: attempt * 6,
          });
          setItems((prev) => prev.map((x) => (x.id === id ? g : x)));
        },
        controller.signal
      );
      setItems((prev) => prev.map((x) => (x.id === id ? finalGen : x)));
      toast.success("Video ready");
      setVideoPrompt("");
      if (activeSessionId && finalGen.file_url) {
        try {
          await sessionsApi.appendMessage(activeSessionId, {
            role: "assistant",
            text: `Here's your ${duration}s clip.`,
            attachment: {
              kind: "video",
              file_url: finalGen.file_url,
              prompt: p,
              generation_id: id,
            },
          });
          window.dispatchEvent(new CustomEvent("glitch:transcript-refresh"));
        } catch (_) {
          /* ignore */
        }
      }
    } catch (e) {
      toast.error(e.message || "Video generation failed");
    } finally {
      setLoading(false);
      setPending(null);
      abortRef.current = null;
    }
  };

  const remove = async (id) => {
    try {
      await generationsApi.remove(id);
      setItems((prev) => prev.filter((x) => x.id !== id));
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const currentPrompt = tab === "image" ? imagePrompt : videoPrompt;
  const setCurrentPrompt = tab === "image" ? setImagePrompt : setVideoPrompt;

  const savePrompt = async () => {
    const text = currentPrompt.trim();
    if (!text) return;
    try {
      const res = await promptsApi.create({ kind: tab, text });
      setPrompts((prev) => [res.prompt, ...prev]);
      toast.success("Prompt saved to Library");
    } catch (e) {
      toast.error("Could not save prompt");
    }
  };

  const applyPrompt = (p) => {
    if (p.kind === "video") {
      setTab("video");
      setVideoPrompt(p.text);
    } else {
      setTab("image");
      setImagePrompt(p.text);
    }
  };

  const removePrompt = async (id) => {
    try {
      await promptsApi.remove(id);
      setPrompts((prev) => prev.filter((x) => x.id !== id));
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const relabelPrompt = async (id, label) => {
    try {
      const res = await promptsApi.update(id, { label });
      setPrompts((prev) => prev.map((p) => (p.id === id ? res.prompt : p)));
    } catch (e) {
      toast.error("Update failed");
    }
  };

  const submit = () => (tab === "image" ? generateImage() : generateVideo());

  const gallery = items.filter((i) => i.kind === tab);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="studio-panel"
        className="glass-strong border-white/10 w-[95vw] max-w-3xl max-h-[92vh] overflow-y-auto overflow-x-hidden p-4 sm:p-6 grid-cols-[minmax(0,1fr)]"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <Wand2 className="h-5 w-5 text-blue-300" />
            Glitch Studio
          </DialogTitle>
          <DialogDescription className="text-white/60">
            Cook up images with Nano Banana 2 or short clips with Veo 3.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList
            data-testid="studio-tabs"
            className="bg-black/30 border border-white/10"
          >
            <TabsTrigger
              value="image"
              data-testid="studio-tab-image"
              className="data-[state=active]:bg-blue-600/25 data-[state=active]:text-white"
            >
              <ImagePlus className="h-3.5 w-3.5 mr-1.5 text-emerald-300" />
              Image
            </TabsTrigger>
            <TabsTrigger
              value="video"
              data-testid="studio-tab-video"
              className="data-[state=active]:bg-blue-600/25 data-[state=active]:text-white"
            >
              <Video className="h-3.5 w-3.5 mr-1.5 text-purple-300" />
              Video
            </TabsTrigger>
            <TabsTrigger
              value="library"
              data-testid="studio-tab-library"
              className="data-[state=active]:bg-blue-600/25 data-[state=active]:text-white"
            >
              <Library className="h-3.5 w-3.5 mr-1.5 text-amber-300" />
              Library
            </TabsTrigger>
          </TabsList>

          <TabsContent value="image" className="mt-3 space-y-2">
            <Textarea
              data-testid="image-prompt-input"
              value={imagePrompt}
              onChange={(e) => setImagePrompt(e.target.value)}
              rows={2}
              placeholder="e.g. A cinematic wide shot of a red desert with a glowing blue orb hovering at dusk…"
              className="bg-black/40 border-white/10 focus-visible:ring-blue-500/40"
            />
            <div className="flex flex-wrap items-center gap-2">
              <Select value={imageModel} onValueChange={setImageModel}>
                <SelectTrigger
                  data-testid="image-model-select"
                  className="w-full sm:w-64 bg-black/40 border-white/10 text-xs"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="glass-strong border-white/10">
                  {IMAGE_MODELS.map((m) => (
                    <SelectItem key={m.value} value={m.value}>
                      {m.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="text-[10px] text-white/40 uppercase tracking-widest">
                Model
              </span>
            </div>
            <AspectPicker
              value={imageAspect}
              onChange={setImageAspect}
              testIdPrefix="image-aspect"
            />

            {/* Pick any uploaded image as a reference for Nano Banana Edit */}
            <div
              data-testid="uploaded-ref-picker"
              className="rounded-xl border border-white/10 bg-black/25 p-2.5 space-y-2"
            >
              {uploadedRefImage ? (
                <div className="flex items-center gap-2">
                  <ImageIcon className="h-4 w-4 text-blue-300 shrink-0" />
                  <div className="text-xs text-white/85 truncate flex-1">
                    Referencing <span className="text-blue-200">{uploadedRefImage.filename}</span>
                    <span className="text-white/40 ml-1">
                      (switches to Nano Banana Edit)
                    </span>
                  </div>
                  <button
                    type="button"
                    data-testid="uploaded-ref-clear"
                    onClick={() => setUploadedRefImage(null)}
                    className="text-white/50 hover:text-white transition-colors text-xs"
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  data-testid="uploaded-ref-pick"
                  onClick={() => setFilePickerOpen(true)}
                  className="flex items-center gap-2 text-xs text-white/70 hover:text-white transition-colors"
                >
                  <ImageIcon className="h-4 w-4 text-blue-300" />
                  Iterate on one of your uploaded photos…
                </button>
              )}
            </div>

            {(user?.avatars?.length || 0) > 0 && (
              <div
                data-testid="avatar-picker-image"
                className="rounded-xl border border-white/10 bg-black/25 p-2.5 space-y-2"
              >
                <label className="flex items-center gap-2 text-xs text-white/80 cursor-pointer select-none">
                  <input
                    data-testid="use-avatar-image-toggle"
                    type="checkbox"
                    checked={useAvatarImage}
                    onChange={(e) => {
                      setUseAvatarImage(e.target.checked);
                      if (e.target.checked && !selectedAvatarImage) {
                        setSelectedAvatarImage(user.avatars[0].id);
                      }
                    }}
                    className="accent-blue-500"
                  />
                  <span>
                    Use one of my avatars
                    <span className="text-white/40 ml-1">
                      (switches to Nano Banana Edit)
                    </span>
                  </span>
                </label>
                {useAvatarImage && (
                  <div className="flex flex-wrap gap-1.5 pl-6">
                    {user.avatars.map((a) => (
                      <button
                        key={a.id}
                        type="button"
                        data-testid={`avatar-chip-image-${a.id}`}
                        onClick={() => setSelectedAvatarImage(a.id)}
                        className={`relative rounded-lg overflow-hidden border transition-all ${
                          selectedAvatarImage === a.id
                            ? "border-blue-400 ring-2 ring-blue-400/40"
                            : "border-white/10 opacity-70 hover:opacity-100"
                        }`}
                        title={a.label}
                      >
                        <img
                          src={a.url}
                          alt={a.label}
                          className="h-10 w-10 object-cover"
                        />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            <div className="flex flex-wrap justify-between items-center gap-2">
              <Button
                data-testid="save-prompt-btn"
                variant="ghost"
                onClick={savePrompt}
                disabled={!imagePrompt.trim()}
                className="text-white/70 hover:text-white hover:bg-white/5"
              >
                <BookmarkPlus className="h-4 w-4 mr-1.5" /> Save prompt
              </Button>
              <Button
                data-testid="generate-image-btn"
                onClick={generateImage}
                disabled={!imagePrompt.trim() || loading}
                className="bg-blue-600 hover:bg-blue-500"
              >
                {loading && tab === "image" ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Drawing…
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 mr-2" /> Generate image
                  </>
                )}
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="video" className="mt-3 space-y-2">
            <Textarea
              data-testid="video-prompt-input"
              value={videoPrompt}
              onChange={(e) => setVideoPrompt(e.target.value)}
              rows={2}
              placeholder="e.g. A short animated clip of a paper airplane gliding over a neon skyline…"
              className="bg-black/40 border-white/10 focus-visible:ring-blue-500/40"
            />
            <div className="flex flex-wrap items-center gap-2">
              <Select value={videoModel} onValueChange={setVideoModel}>
                <SelectTrigger
                  data-testid="video-model-select"
                  className="w-full sm:w-64 bg-black/40 border-white/10 text-xs"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="glass-strong border-white/10">
                  {VIDEO_MODELS.map((m) => (
                    <SelectItem key={m.value} value={m.value}>
                      {m.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="text-[10px] text-white/40 uppercase tracking-widest">
                Model
              </span>
            </div>
            <AspectPicker
              value={videoAspect}
              onChange={setVideoAspect}
              testIdPrefix="video-aspect"
            />
            {(user?.avatars?.length || 0) > 0 && (
              <div
                data-testid="avatar-picker-video"
                className="rounded-xl border border-white/10 bg-black/25 p-2.5 space-y-2"
              >
                <label className="flex items-center gap-2 text-xs text-white/80 cursor-pointer select-none">
                  <input
                    data-testid="use-avatar-video-toggle"
                    type="checkbox"
                    checked={useAvatarVideo}
                    onChange={(e) => {
                      setUseAvatarVideo(e.target.checked);
                      if (e.target.checked && !selectedAvatarVideo) {
                        setSelectedAvatarVideo(user.avatars[0].id);
                      }
                    }}
                    className="accent-blue-500"
                  />
                  <span>
                    Animate one of my avatars
                    <span className="text-white/40 ml-1">
                      (switches to Veo 3 image-to-video)
                    </span>
                  </span>
                </label>
                {useAvatarVideo && (
                  <div className="flex flex-wrap gap-1.5 pl-6">
                    {user.avatars.map((a) => (
                      <button
                        key={a.id}
                        type="button"
                        data-testid={`avatar-chip-video-${a.id}`}
                        onClick={() => setSelectedAvatarVideo(a.id)}
                        className={`relative rounded-lg overflow-hidden border transition-all ${
                          selectedAvatarVideo === a.id
                            ? "border-blue-400 ring-2 ring-blue-400/40"
                            : "border-white/10 opacity-70 hover:opacity-100"
                        }`}
                        title={a.label}
                      >
                        <img
                          src={a.url}
                          alt={a.label}
                          className="h-10 w-10 object-cover"
                        />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            <div className="flex flex-wrap items-center gap-2 justify-between">
              <Button
                data-testid="save-video-prompt-btn"
                variant="ghost"
                onClick={savePrompt}
                disabled={!videoPrompt.trim()}
                className="text-white/70 hover:text-white hover:bg-white/5"
              >
                <BookmarkPlus className="h-4 w-4 mr-1.5" /> Save prompt
              </Button>
              <div className="flex items-center gap-2 flex-wrap ml-auto">
                <Select
                  value={String(duration)}
                  onValueChange={(v) => setDuration(Number(v))}
                >
                  <SelectTrigger
                    data-testid="video-duration-select"
                    className="w-32 bg-black/40 border-white/10"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="glass-strong border-white/10">
                    <SelectItem value="4">4 seconds</SelectItem>
                    <SelectItem value="6">6 seconds</SelectItem>
                    <SelectItem value="8">8 seconds</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  data-testid="generate-video-btn"
                  onClick={generateVideo}
                  disabled={!videoPrompt.trim() || loading}
                  className="bg-blue-600 hover:bg-blue-500"
                >
                  {loading && tab === "video" ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Rendering{pending?.elapsed ? ` · ${pending.elapsed}s` : "…"}
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4 mr-2" /> Generate video
                    </>
                  )}
                </Button>
              </div>
            </div>
            {pending?.kind === "video" && (
              <div className="text-[11px] text-blue-300/70 italic">
                Veo 3 typically takes 30–90s. Feel free to keep talking to
                Glitch while it renders.
              </div>
            )}
          </TabsContent>

          <TabsContent value="library" className="mt-3">
            {prompts.length === 0 ? (
              <div className="text-sm text-white/40 py-8 text-center border border-dashed border-white/10 rounded-lg">
                Save your favorite prompts and they&apos;ll live here for one-tap
                reruns.
              </div>
            ) : (
              <div className="space-y-2 max-h-[50vh] overflow-y-auto pr-1">
                <AnimatePresence initial={false}>
                  {prompts.map((p) => (
                    <motion.div
                      key={p.id}
                      layout
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      data-testid={`prompt-${p.id}`}
                      className="group rounded-lg border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] px-3 py-2.5 flex items-start gap-2 transition-colors"
                    >
                      {p.kind === "image" ? (
                        <ImagePlus className="h-3.5 w-3.5 shrink-0 mt-1 text-emerald-300" />
                      ) : (
                        <Video className="h-3.5 w-3.5 shrink-0 mt-1 text-purple-300" />
                      )}
                      <div className="flex-1 min-w-0">
                        <Input
                          data-testid={`prompt-label-${p.id}`}
                          value={p.label || ""}
                          onChange={(e) =>
                            setPrompts((prev) =>
                              prev.map((x) =>
                                x.id === p.id
                                  ? { ...x, label: e.target.value }
                                  : x
                              )
                            )
                          }
                          onBlur={(e) => relabelPrompt(p.id, e.target.value)}
                          placeholder="Add a label…"
                          className="h-7 text-xs bg-transparent border-none px-0 focus-visible:ring-0 text-white/90 font-medium"
                        />
                        <div className="text-[11px] text-white/60 whitespace-pre-wrap line-clamp-2">
                          {p.text}
                        </div>
                      </div>
                      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          data-testid={`use-prompt-${p.id}`}
                          onClick={() => applyPrompt(p)}
                          className="h-7 w-7 rounded-md hover:bg-blue-500/20 flex items-center justify-center"
                          aria-label="Use prompt"
                          title="Use this prompt"
                        >
                          <Play className="h-3.5 w-3.5 text-blue-300" />
                        </button>
                        <button
                          data-testid={`delete-prompt-${p.id}`}
                          onClick={() => removePrompt(p.id)}
                          className="h-7 w-7 rounded-md hover:bg-red-500/20 flex items-center justify-center"
                          aria-label="Delete"
                        >
                          <Trash2 className="h-3.5 w-3.5 text-red-400/80" />
                        </button>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Gallery */}
        {tab !== "library" && (
          <div className="mt-4 max-h-[45vh] overflow-y-auto overflow-x-hidden pr-1">
            <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 mb-2">
              {tab === "image" ? "Recent images" : "Recent videos"}
            </div>
            {gallery.length === 0 && (
              <div className="text-sm text-white/40 py-6 text-center border border-dashed border-white/10 rounded-lg">
                Nothing yet. Prompt above to create your first {tab}.
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <AnimatePresence initial={false}>
                {gallery.map((g) => (
                  <motion.div
                    key={g.id}
                    layout
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    data-testid={`gen-${g.id}`}
                    className="group rounded-xl border border-white/10 bg-white/[0.03] overflow-hidden relative min-w-0"
                  >
                    {g.kind === "image" && g.file_url && (
                      <img
                        src={mediaUrl(g.file_url)}
                        alt={g.prompt}
                        className="w-full max-w-full aspect-square object-cover bg-black/40"
                      />
                    )}
                    {g.kind === "video" && g.file_url && (
                      <video
                        src={mediaUrl(g.file_url)}
                        controls
                        className="w-full max-w-full aspect-video bg-black/40 block"
                      />
                    )}
                    {g.status === "pending" && (
                      <div className="aspect-square flex items-center justify-center">
                        <Loader2 className="h-6 w-6 animate-spin text-blue-300" />
                      </div>
                    )}
                    {g.status === "failed" && (
                      <div className="aspect-square flex items-center justify-center text-xs text-red-300 px-3 text-center">
                        Failed: {g.error?.slice(0, 80) || "Unknown error"}
                      </div>
                    )}
                    <div className="p-2.5 text-[11px] text-white/70 line-clamp-2">
                      {g.prompt}
                    </div>
                    <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        data-testid={`save-gen-prompt-${g.id}`}
                        onClick={async () => {
                          try {
                            const res = await promptsApi.create({
                              kind: g.kind,
                              text: g.prompt,
                            });
                            setPrompts((prev) => [res.prompt, ...prev]);
                            toast.success("Prompt saved to Library");
                          } catch (e) {
                            toast.error("Save failed");
                          }
                        }}
                        className="h-7 w-7 rounded-md bg-black/60 hover:bg-blue-500/60 flex items-center justify-center"
                        aria-label="Save prompt"
                        title="Save this prompt"
                      >
                        <Bookmark className="h-3.5 w-3.5 text-white/80" />
                      </button>
                      {g.file_url && (
                        <a
                          href={mediaUrl(g.file_url)}
                          download
                          onClick={(e) => e.stopPropagation()}
                          className="h-7 w-7 rounded-md bg-black/60 hover:bg-black/80 flex items-center justify-center"
                          aria-label="Download"
                        >
                          <Download className="h-3.5 w-3.5 text-white/80" />
                        </a>
                      )}
                      <button
                        data-testid={`delete-gen-${g.id}`}
                        onClick={() => remove(g.id)}
                        className="h-7 w-7 rounded-md bg-black/60 hover:bg-red-500/60 flex items-center justify-center"
                        aria-label="Delete"
                      >
                        <Trash2 className="h-3.5 w-3.5 text-white/80" />
                      </button>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          </div>
        )}
      </DialogContent>
      <FilePicker
        open={filePickerOpen}
        onOpenChange={setFilePickerOpen}
        onPick={(f) =>
          setUploadedRefImage({
            id: f.id,
            filename: f.filename,
            content_type: f.content_type,
          })
        }
        filter="image"
        title="Pick a reference photo"
      />
    </Dialog>
  );
};
